import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  reactQueryGet,
  reactQueryPatch,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Post } from "@circle-react/types/Post";

export const REQUEST_KEY = "post-conversation-summary";

interface UsePostConversationSummaryProps {
  post: Post;
}

interface AiSummary {
  content: string;
  overview?: string;
}

export const usePostConversationSummary = ({
  post,
}: UsePostConversationSummaryProps) => {
  const queryKey = [REQUEST_KEY, post.id];
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useQuery<AiSummary>(
    queryKey,
    () =>
      reactQueryGet(
        internalApi.posts.conversationSummary.show({
          postId: String(post.id),
        }),
      ),
    {
      enabled: false, // Don't auto-fetch when mounted
      staleTime: 10 * 60 * 1000, // 10 minutes
      retry: (_, error: any) => error?.status !== 404,
    },
  );

  const clearCache = () => {
    void queryClient.resetQueries({ queryKey, exact: true });
  };

  const updateConversationSummary = useMutation(
    (params: { disabled: boolean }) =>
      reactQueryPatch(
        internalApi.posts.conversationSummary.update({
          postId: String(post.id),
        }),
        params,
      ),
    {
      onSuccess: () => {
        clearCache();
      },
    },
  );

  return {
    conversationSummary: data,
    isLoadingConversationSummary: isLoading,
    refetchConversationSummary: refetch,
    clearConversationSummary: clearCache,
    updateConversationSummary: updateConversationSummary.mutateAsync,
    isSaving: updateConversationSummary.isLoading,
  };
};
